import { Controller } from "@hotwired/stimulus"

// "Deal from the deck" entrance animation (3.8e) runs the first time a
// comparison table paints in this page session — never on filter re-renders or
// Turbo frame navigations. The module-level flag persists across the
// connect/disconnect churn that filtering causes; only a genuine full page load
// resets it. (Deliberately not session/localStorage — re-arriving at /search
// later and seeing it deal again is fine.)
let hasDealt = false

// EID-4 comparison table: deals the room columns in on first paint, then
// collapses perk rows where no room has the feature.
//
// Perk cells lazy-load per room (each room column is an independent turbo-frame),
// so collapse can only run client-side once the cells arrive. After every frame
// loads we re-evaluate: a perk row is hidden only once ALL room columns have
// reported in (matching cell count === room count) and none has the feature.
// Both the label cell and every matching feature cell are toggled together, so
// the fixed-height rows stay aligned across columns.
export default class extends Controller {
  connect() {
    this.collapse = this.collapse.bind(this)
    this.dealOnce()
    // Re-evaluate as each per-room perk frame finishes loading.
    this.element.addEventListener("turbo:frame-load", this.collapse)
    // Catch any frames already loaded before this controller connected.
    this.collapse()
  }

  disconnect() {
    this.element.removeEventListener("turbo:frame-load", this.collapse)
  }

  // Deal the room columns in from the left with a slight tilt — once per page
  // session. Honours prefers-reduced-motion (no-op) and contains horizontal
  // overflow during the run so the off-canvas start can't flash a scrollbar.
  dealOnce() {
    if (hasDealt) return
    hasDealt = true

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    const columns = this.element.querySelectorAll(".comparison-col:not(.comparison-col--labels)")
    if (columns.length === 0) return

    columns.forEach((col, i) => col.style.setProperty("--comparison-col-index", i))

    // Hide horizontal overflow during the deal; restore once the last column lands.
    this.element.classList.add("comparison-table--dealing", "comparison-table--animate")
    let remaining = columns.length
    const onLanded = (event) => {
      if (event.animationName !== "comparison-deal-in") return
      remaining -= 1
      if (remaining > 0) return
      this.element.classList.remove("comparison-table--dealing")
      this.element.removeEventListener("animationend", onLanded)
    }
    this.element.addEventListener("animationend", onLanded)
  }

  collapse() {
    const roomCount = this.element.querySelectorAll(
      ".comparison-col:not(.comparison-col--labels)"
    ).length
    if (roomCount === 0) return

    this.element.querySelectorAll("[data-row-key]").forEach((label) => {
      const cells = this.element.querySelectorAll(
        `[data-feature-key="${label.dataset.rowKey}"]`
      )
      // No lazy cells for this key (e.g. the inline room-type row) — leave it.
      if (cells.length === 0) return
      // Undecided until every room column has reported its cell.
      if (cells.length < roomCount) return

      const anyPresent = Array.from(cells).some(
        (cell) => cell.dataset.featurePresent === "true"
      )
      this.toggleRow(label, cells, anyPresent)
    })
  }

  toggleRow(label, cells, visible) {
    const display = visible ? "" : "none"
    label.style.display = display
    cells.forEach((cell) => (cell.style.display = display))
  }
}
